import React, { useState, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Bug, Lightbulb, X, Loader2, Paperclip, Mic, MicOff } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

function VoiceButton({ onTranscript, className = '' }) {
    const [listening, setListening] = useState(false);
    const recognitionRef = useRef(null);

    const toggle = useCallback(() => {
        if (listening) {
            recognitionRef.current?.stop();
            setListening(false);
            return;
        }

        if (!SpeechRecognition) {
            toast.error('Voice input not supported in this browser');
            return;
        }

        const recognition = new SpeechRecognition();
        recognition.continuous = true;
        recognition.interimResults = false;
        recognition.lang = 'en-US';

        recognition.onresult = (event) => {
            let transcript = '';
            for (let i = event.resultIndex; i < event.results.length; i++) {
                if (event.results[i].isFinal) {
                    transcript += event.results[i][0].transcript;
                }
            }
            if (transcript) onTranscript(transcript.trim());
        };

        recognition.onerror = (e) => {
            if (e.error !== 'aborted') toast.error(`Voice error: ${e.error}`);
            setListening(false);
        };

        recognition.onend = () => setListening(false);

        recognitionRef.current = recognition;
        recognition.start();
        setListening(true);
    }, [listening, onTranscript]);

    if (!SpeechRecognition) return null;

    return (
        <button type="button" onClick={toggle} title={listening ? 'Stop recording' : 'Voice input'}
            className={`p-1.5 rounded-lg transition-colors ${listening ? 'bg-red-100 text-red-600 animate-pulse' : 'bg-stone-100 text-stone-500 hover:bg-stone-200 hover:text-stone-700'} ${className}`}>
            {listening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
        </button>
    );
}

const PRIORITIES = [
    { value: 'low', label: 'Low', color: 'bg-stone-100 text-stone-600' },
    { value: 'medium', label: 'Medium', color: 'bg-amber-100 text-amber-700' },
    { value: 'high', label: 'High', color: 'bg-orange-100 text-orange-700' },
    { value: 'critical', label: 'Critical', color: 'bg-red-100 text-red-700' },
];

const CATEGORIES = [
    { value: 'ui', label: 'UI' }, { value: 'backend', label: 'Backend' },
    { value: 'performance', label: 'Performance' }, { value: 'integration', label: 'Integration' },
    { value: 'data', label: 'Data' }, { value: 'other', label: 'Other' },
];

export default function BugReportModal({ open, onClose, screenshotDataUrl = null, pageUrl = '' }) {
    const { getAuthHeader } = useAuth();
    const [type, setType] = useState(screenshotDataUrl ? 'bug' : 'feature');
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [priority, setPriority] = useState(screenshotDataUrl ? 'high' : 'medium');
    const [category, setCategory] = useState('other');
    const [attachments, setAttachments] = useState(screenshotDataUrl ? [screenshotDataUrl] : []);
    const [submitting, setSubmitting] = useState(false);

    const handleSubmit = async () => {
        if (!title.trim() || title.trim().length < 5) {
            toast.error('Title must be at least 5 characters');
            return;
        }
        if (!description.trim() || description.trim().length < 10) {
            toast.error('Description must be at least 10 characters');
            return;
        }

        setSubmitting(true);
        try {
            await axios.post(`${API}/feature-requests/`, {
                title: title.trim(),
                description: description.trim(),
                type,
                priority,
                category,
                page_url: pageUrl || window.location.href,
                attachments,
            }, { headers: getAuthHeader() });

            toast.success(type === 'bug' ? 'Bug report submitted!' : 'Feature request submitted!');
            setTitle('');
            setDescription('');
            setAttachments([]);
            onClose();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to submit');
        } finally {
            setSubmitting(false);
        }
    };

    const removeAttachment = (i) => setAttachments(a => a.filter((_, idx) => idx !== i));

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        {type === 'bug' ? <Bug className="w-5 h-5 text-red-500" /> : <Lightbulb className="w-5 h-5 text-amber-500" />}
                        {type === 'bug' ? 'Report a Bug' : 'Request a Feature'}
                    </DialogTitle>
                </DialogHeader>

                <div className="space-y-4 py-2">
                    {/* Type toggle */}
                    <div className="bg-stone-100 rounded-lg p-0.5 flex">
                        <button onClick={() => setType('bug')}
                            className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                                type === 'bug' ? 'bg-white shadow-sm text-red-600' : 'text-stone-500'
                            }`}>
                            <Bug className="w-3.5 h-3.5" /> Bug Report
                        </button>
                        <button onClick={() => setType('feature')}
                            className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                                type === 'feature' ? 'bg-white shadow-sm text-amber-600' : 'text-stone-500'
                            }`}>
                            <Lightbulb className="w-3.5 h-3.5" /> Feature Request
                        </button>
                    </div>

                    {/* Priority + Category */}
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="block text-xs font-medium text-stone-600 mb-1">Priority</label>
                            <div className="flex gap-1">
                                {PRIORITIES.map(p => (
                                    <button key={p.value} type="button" onClick={() => setPriority(p.value)}
                                        className={`flex-1 px-2 py-1.5 text-[10px] font-medium rounded-md transition-colors ${
                                            priority === p.value ? p.color + ' ring-1 ring-current' : 'bg-stone-50 text-stone-400'
                                        }`}>
                                        {p.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-stone-600 mb-1">Category</label>
                            <select value={category} onChange={e => setCategory(e.target.value)}
                                className="w-full px-3 py-1.5 text-xs border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20">
                                {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                            </select>
                        </div>
                    </div>

                    {/* Page URL indicator */}
                    {pageUrl && (
                        <div className="text-[10px] text-stone-400 bg-stone-50 px-2 py-1 rounded">
                            Reporting from: <span className="font-mono">{pageUrl.replace(window.location.origin, '')}</span>
                        </div>
                    )}

                    {/* Title */}
                    <div>
                        <label className="block text-xs font-medium text-stone-600 mb-1">Title</label>
                        <div className="flex items-center gap-1.5">
                            <input value={title} onChange={e => setTitle(e.target.value)}
                                placeholder={type === 'bug' ? 'Brief description of the bug...' : 'What feature would you like?'}
                                className="flex-1 px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20"
                                autoFocus />
                            <VoiceButton onTranscript={t => setTitle(prev => prev ? `${prev} ${t}` : t)} />
                        </div>
                    </div>

                    {/* Description */}
                    <div>
                        <div className="flex items-center justify-between mb-1">
                            <label className="text-xs font-medium text-stone-600">Description</label>
                            <VoiceButton onTranscript={t => setDescription(prev => prev ? `${prev} ${t}` : t)} />
                        </div>
                        <textarea value={description} onChange={e => setDescription(e.target.value)}
                            rows={3}
                            placeholder={type === 'bug' ? 'Steps to reproduce, expected vs actual behavior...' : 'Describe the feature and why it would be useful...'}
                            className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20" />
                    </div>

                    {/* Attachments */}
                    {attachments.length > 0 && (
                        <div>
                            <label className="block text-xs font-medium text-stone-600 mb-1">
                                <Paperclip className="w-3.5 h-3.5 inline mr-1" />
                                Screenshots ({attachments.length})
                            </label>
                            <div className="flex gap-2 flex-wrap">
                                {attachments.map((att, i) => (
                                    <div key={i} className="relative group">
                                        <img src={att} alt={`Screenshot ${i + 1}`}
                                            className="w-32 h-20 object-cover rounded-lg border border-stone-200" />
                                        <button onClick={() => removeAttachment(i)}
                                            className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                            <X className="w-3 h-3" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button onClick={handleSubmit} disabled={submitting}
                        className={type === 'bug' ? 'bg-red-600 hover:bg-red-700' : ''}>
                        {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                        {submitting ? 'Submitting...' : 'Submit'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
